import { Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { Observable, throwError } from "rxjs";
import { HttpClient, HttpHeaders } from "@angular/common/http";
import { CookieService } from "ngx-cookie-service";
import { catchError } from "rxjs/operators";
import { AppSettingsService } from "./../app-settings.service";

const apiEndpoints = {
  countries: "openlookup/country",
  zipCodeLookup: "openlookup/zipcode/",
  streamData: "horizon/232",
  state: "openlookup/state",
  credentials: "openlookup/credentialType",
  stateLicensures: "openlookup/stateLicensures",
  serviceCodesByCredentials: "openlookup/servicecodes?q={params}",
  freeServices: "openlookup/freeservices/{personId}",
  lov: "openLookup/getLov/",
  horizon: "horizon",
  chatStatus: "openlookup/chatStatus",
};

@Injectable()
export class CommonApiService {
  private baseUrl: string;
  private authorizationBearer: string | null;

  constructor(
    private cookieService: CookieService,
    private appSettings: AppSettingsService,
    private angularHttp: HttpClient,
    private router: Router
  ) {
    this.baseUrl = this.appSettings.CommonApiBaseUrl;
    this.setAuthorizationBearer();
  }

  private setHeaders(): HttpHeaders {
    let headers = new HttpHeaders({
      "Content-Type": "application/json",
      Accept: "application/json",
    });

    if (this.authorizationBearer) {
      headers = headers.set("Authorization", `Bearer ${this.authorizationBearer}`);
    }

    return headers;
  }

  get(endpoint: string): Observable<any> {
    const url = this.baseUrl.concat(endpoint);
    return this.angularHttp.get(url, { headers: this.setHeaders() }).pipe(
      catchError((error) => throwError(error))
    );
  }

  getBlob(endpoint: string): Observable<Blob> {
    const url = this.baseUrl.concat(endpoint);
    return this.angularHttp.get(url, { headers: this.setHeaders(), responseType: "blob" }).pipe(
      catchError((error) => throwError(error))
    );
  }

  setDocumentStream() {
    return this.get(apiEndpoints.streamData);
  }

  getCountries() {
    return this.get(apiEndpoints.countries);
  }

  getStates() {
    return this.get(apiEndpoints.state);
  }

  getCredentials() {
    return this.get(apiEndpoints.credentials);
  }

  getLov(key: string) {
    return this.get(apiEndpoints.lov + key);
  }

  getChatStatus(): Observable<any> {
    return this.get(apiEndpoints.chatStatus);
  }

  getStateLicensures() {
    return this.get(apiEndpoints.stateLicensures);
  }

  getServiceCodesByCredentials(params: string) {
    const endpoint = apiEndpoints.serviceCodesByCredentials.replace("{params}", params);
    return this.get(endpoint);
  }

  getFreeServices(personId: string) {
    const endpoint = apiEndpoints.freeServices.replace("{personId}", personId);
    return this.get(endpoint);
  }

  lookupStateAndCityByZipCode(zipcode: string) {
    const endpoint = apiEndpoints.zipCodeLookup + zipcode;
    return this.get(endpoint);
  }

  getHorizonsList(): Observable<any> {
    return this.get(apiEndpoints.horizon);
  }

  getHorizonsFile(id: number): Observable<Blob> {
    return this.getBlob(apiEndpoints.horizon.concat(`/${id}`));
  }

  private setAuthorizationBearer() {
    const customCookieName = localStorage.getItem("customCookieName");
    if (customCookieName) {
      this.authorizationBearer = this.cookieService.get(customCookieName);
    } else {
      this.authorizationBearer = null;
    }
  }
}